import { motion } from "framer-motion";
import LandingLayout from '@/components/LandingLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, MessageCircle, FileText, Github } from "lucide-react";
import { SEO } from '@/components/SEO';

export const faqs = [
  {
    question: "How do I get started with StatusBot?",
    answer: "Getting started is easy! Sign up for an account, and our onboarding wizard will guide you through setting up your first project and monitor. You can also check out our comprehensive documentation for detailed guides."
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards (Visa, MasterCard, American Express) and support payment via PayPal. For Enterprise plans, we also offer invoice-based billing."
  },
  {
    question: "Can I change my plan later?",
    answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any payments accordingly."
  },
  {
    question: "How does the AI monitoring work?",
    answer: "Our AI monitoring system uses advanced machine learning algorithms to detect anomalies in your service performance. It learns from historical data to predict potential issues before they impact your users."
  },
  {
    question: "What kind of support do you offer?",
    answer: "We offer multiple support channels including email support, documentation, and community forums. Enterprise customers receive priority support with dedicated response times."
  }
];

export const faqsExtra = [
  {
    question: "What is StatusBot Pro?",
    answer: "StatusBot Pro is an enhanced version of the core StatusBot service that unlocks additional features, higher usage limits, and priority support. With Pro, organizations and advanced users can monitor more services, manage more incidents, and customize status pages more flexibly."
  },
  {
    question: "What features are included with Pro?",
    answer: "Here are some of the benefits you'll get by upgrading:\n\n• Increased limits on monitors, incidents, and status pages\n• Custom domain support for status pages\n• More status roles and integration options\n• Custom webhooks and advanced alerting\n• Access to exclusive features (e.g. historical uptime analytics, priority support)\n• Ability to use custom buttons and richer message editors in status posts\n\nThese features help larger or more complex operations scale StatusBot usage beyond the constraints of the free tier."
  },
  {
    question: "How do I activate my Pro subscription?",
    answer: "To activate Pro:\n\n1. Log in to your StatusBot dashboard.\n2. Navigate to the \"Upgrade\" or \"Billing / Pro\" section.\n3. Select the desired plan and submit payment via supported methods.\n4. Your account will be upgraded instantly (or within a short delay).\n\nYou'll then have access to the enhanced limits and features immediately."
  },
  {
    question: "Can I cancel or change my Pro subscription?",
    answer: "Yes — you can cancel or modify your subscription at any time:\n\n• To downgrade or upgrade, visit your billing settings and choose the new plan. The change will often take effect at the next billing cycle or immediately (depending on status).\n• To cancel, you can stop auto-renewal in your settings. You'll retain Pro features until the end of your current billing period.\n\nYou generally won't receive a refund for the remainder of the term unless otherwise stated in the refund policy."
  },
  {
    question: "Will I get a refund if I cancel?",
    answer: "Refunds depend on your plan and the timing of cancellation. In many cases, partial refunds are not offered for unused portions of a subscription term. Always review the refund policy or contact us directly if you believe your situation warrants an exception."
  },
  {
    question: "Is my payment information secure?",
    answer: "Yes. StatusBot works with trusted payment processors and adopts industry best practices to protect your financial data. Your card details are handled securely, and we do not store raw payment information on our servers."
  },
  {
    question: "What happens if my Pro subscription expires?",
    answer: "If your Pro term ends (due to cancellation or nonpayment), your account will revert to the free plan. At that point:\n\n• You may lose access to features or limits exclusive to Pro.\n• Monitors, incidents, or status pages exceeding free-tier limits may be disabled or paused.\n• You'll still retain all data (status logs, configuration) and can restore it if you re-upgrade later.\n\nWe encourage all users to monitor usage so that downgrades don't unexpectedly disrupt critical monitoring."
  },
  {
    question: "Can I upgrade or downgrade plans?",
    answer: "Yes, plan switching is supported. You can upgrade or downgrade based on your needs, either from your billing dashboard or account settings. Depending on the billing model, you may be prorated or adjusted to reflect the change mid-term."
  },
  {
    question: "How can I contact support for Pro-related issues?",
    answer: "We provide support via:\n\n• A dedicated support email or ticketing system (available to all users)\n• Priority support for Pro customers (faster response times)\n• A support community forum where you can ask questions, report bugs, and get help from staff or community members\n\nBe sure to include your account ID, relevant logs, or screenshots when submitting a support request to expedite resolution."
  }
];

const supportOptions = [
  {
    icon: <Mail className="w-6 h-6" />,
    title: "Email Support",
    description: "Get help from our support team",
    action: "Email Us",
    link: "mailto:support@status.bot"
  },
  {
    icon: <MessageCircle className="w-6 h-6" />,
    title: "Community",
    description: "Join our Discord community",
    action: "Join Discord",
    link: "https://discord.gg/statusbot"
  },
  {
    icon: <FileText className="w-6 h-6" />,
    title: "Documentation",
    description: "Browse our detailed guides",
    action: "View Docs",
    link: "/documentation"
  },
  {
    icon: <Github className="w-6 h-6" />,
    title: "GitHub",
    description: "Report issues and contribute",
    action: "Visit GitHub",
    link: "https://github.com/statusbot/statusbot"
  }
];

const Support = () => {
  return (
    <>
      <SEO 
        title="Support Center"
        description="Get help with StatusBot. Access documentation, community support, and contact our team for assistance."
      />
      <LandingLayout>
        <section className="px-4 py-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="container mx-auto max-w-4xl"
          >
            <div className="space-y-8">
              <div className="text-center">
                <h1 className="text-3xl font-bold mb-2">Support Center</h1>
                <p className="text-muted-foreground">
                  Get help with StatusBot. We're here to help you succeed.
                </p>
              </div>

              {/* Support Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {supportOptions.map((option) => (
                  <Card key={option.title} className="border-2 border-violet-500/20">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="p-2 bg-violet-500/10 rounded-lg text-violet-500">
                          {option.icon}
                        </div>
                        <div className="flex-1 space-y-1">
                          <h3 className="font-semibold">{option.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {option.description}
                          </p>
                        </div>
                        <Button variant="ghost" onClick={() => window.location.href = option.link}>
                          {option.action}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* FAQs */}
              <Card>
                <CardHeader>
                  <CardTitle>Frequently Asked Questions</CardTitle>
                  <CardDescription>
                    Quick answers to common questions about StatusBot
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {faqs.map((faq, index) => (
                      <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger>{faq.question}</AccordionTrigger>
                        <AccordionContent>{faq.answer}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>

              {/* Enterprise Support */}
              <Card className="bg-violet-500/5 border-2 border-violet-500/20">
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    <h3 className="text-xl font-semibold">Need Enterprise Support?</h3>
                    <p className="text-muted-foreground">
                      Get priority support, custom solutions, and dedicated resources for your organization.
                    </p>
                    <Button onClick={() => window.location.href = 'mailto:enterprise@status.bot'}>
                      Contact Enterprise Sales
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </section>
      </LandingLayout>
    </>
  );
};

export default Support; 